// src/hooks/useDualStreaming.js

import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api';

const useDualStreaming = () => {
  const [isDualStreaming, setIsDualStreaming] = useState(false);
  const [dualStreamingData, setDualStreamingData] = useState({
    model1: { streamingText: '', modelName: '', tokenCount: 0, modelId: null },
    model2: { streamingText: '', modelName: '', tokenCount: 0, modelId: null }
  });
  const [dualStreamingError, setDualStreamingError] = useState(null);
  const [pendingDualChoice, setPendingDualChoice] = useState(null);
  const [currentUserMessage, setCurrentUserMessage] = useState(null);

  const dualAbortControllerRef = useRef(null);
  const lastDisplayMessage = useRef('');
  const lastFullMessageRef = useRef(''); // Полное сообщение с файлами для сохранения в БД
  const lastFullContentForAI = useRef(''); // Сохраняем полный контекст для AI

  const resetDualStream = useCallback(() => {
    setIsDualStreaming(false);
    setPendingDualChoice(null);
    setDualStreamingError(null);
    setCurrentUserMessage(null);
    lastDisplayMessage.current = '';
    lastFullContentForAI.current = ''; // Очищаем полный контекст
    setDualStreamingData({
      model1: { streamingText: '', modelName: '', tokenCount: 0, modelId: null },
      model2: { streamingText: '', modelName: '', tokenCount: 0, modelId: null }
    });
  }, []);
// Убираем логирование 
  const startDualStreaming = async (chatId, contentForAI, displayContentForUser, modelIds, systemPrompt = null, specificFileNames = null) => {
    try {
      // console.log('🚀 [DUAL STREAM] Начинаем dual streaming:', { chatId, modelIds });
      
      resetDualStream();
      setIsDualStreaming(true);
      setCurrentUserMessage(displayContentForUser); // Устанавливаем текущее сообщение для отображения
      lastDisplayMessage.current = displayContentForUser;
      lastFullMessageRef.current = contentForAI; // Сохраняем полное сообщение сразу
      lastFullContentForAI.current = contentForAI; // Сохраняем полный контекст

      dualAbortControllerRef.current = new AbortController();

      const baseURL = API_CONFIG.getBaseURL();
      const response = await fetch(`${baseURL}/api/chat/${chatId}/messages/dual-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          contentForAI,         // Поле для отправки в AI
          userMessageContent: displayContentForUser, // Поле для сохранения в БД
          modelIds,
          systemPrompt,
          specificFileNames     // Конкретные имена файлов для добавления
        }),
        signal: dualAbortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      const processStream = async () => {
        try {
          let buffer = '';
          
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              console.log('🏁 [DUAL STREAM] Поток завершен');
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Сохраняем неполную строку

            for (const line of lines) {
              if (!line.trim()) continue;
              
              if (line.startsWith('data: ')) {
                try {
                  const jsonStr = line.slice(6).trim();
                  if (jsonStr) {
                    const data = JSON.parse(jsonStr);
                    switch (data.type) {
                    case 'dual_stream_start':
                      setDualStreamingData(prev => ({
                        model1: { ...prev.model1, modelName: data.model1.name, modelId: data.model1.id },
                        model2: { ...prev.model2, modelName: data.model2.name, modelId: data.model2.id }
                      }));
                      break;
                    case 'content':
                      const modelKey = data.modelKey;
                      if (modelKey && (modelKey === 'model1' || modelKey === 'model2')) {
                        setDualStreamingData(prev => ({
                          ...prev,
                          [modelKey]: {
                            ...prev[modelKey],
                            streamingText: data.fullText || prev[modelKey].streamingText + data.content,
                            tokenCount: data.tokenCount || prev[modelKey].tokenCount
                          }
                        }));
                      }
                      break;
                    case 'dual_stream_complete':
                      setIsDualStreaming(false);
                      // Сохраняем полное сообщение для отправки в save-choice
                      // Отключаем избыточное логирование
                      // console.log('🔍 [DUAL STREAM] Завершение dual stream:', {
                      //   dataFullUserMessage: data.fullUserMessage?.substring(0, 100) + '...',
                      //   contentForAI: contentForAI?.substring(0, 100) + '...',
                      //   currentLastFullMessage: lastFullMessageRef.current?.substring(0, 100) + '...'
                      // });
                      lastFullMessageRef.current = data.fullUserMessage || contentForAI;
                      setPendingDualChoice({
                        model1: { content: data.responses.model1.content, modelName: data.responses.model1.modelName, modelId: dualStreamingData.model1.modelId },
                        model2: { content: data.responses.model2.content, modelName: data.responses.model2.modelName, modelId: dualStreamingData.model2.modelId }
                      });
                      break;
                    case 'model_error':
                      if (data.modelKey && (data.modelKey === 'model1' || data.modelKey === 'model2')) {
                        const errorMessage = data.error.includes('Country, region, or territory not supported')
                          ? '🚫 OpenAI API недоступен в вашем регионе. Используйте DeepSeek модели.'
                          : `❌ Ошибка: ${data.error}`;
                        setDualStreamingData(prev => ({
                          ...prev,
                          [data.modelKey]: { ...prev[modelKey], streamingText: errorMessage }
                        }));
                      }
                      break;
                    case 'error':
                      setDualStreamingError(data.message || 'Неизвестная ошибка streaming');
                      setIsDualStreaming(false);
                      break;
                    default:
                      break;
                  }
                    }
                } catch (error) {
                  console.error('❌ [DUAL STREAM] Ошибка парсинга JSON:', error, line);
                }
              }
            }
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('❌ [DUAL STREAM] Ошибка чтения потока:', error);
            setDualStreamingError('Ошибка чтения потока данных');
          }
          setIsDualStreaming(false);
        } finally {
          reader.releaseLock();
        }
      };
      processStream();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('❌ [DUAL STREAM] Ошибка запуска:', error);
        setDualStreamingError(`Ошибка запуска dual streaming: ${error.message}`);
        throw error;
      }
      setIsDualStreaming(false);
    }
  };

  const stopDualStreaming = useCallback(async (chatId) => {
    if (dualAbortControllerRef.current) {
      dualAbortControllerRef.current.abort();
    }
    resetDualStream();
    
    if (chatId) {
      try {
        const response = await axios.post(`/api/chat/${chatId}/messages/cancel-dual-stream`);
        if (response.data.chat) {
          return response.data.chat;
        }
      } catch (error) {
        console.error('Ошибка отмены dual streaming на сервере:', error);
      }
    }
    return null;
  }, [resetDualStream]);
// Убираем логирование в этом методе
  const saveChosenResponse = async (chatId, modelKey, content, modelId) => {
    try {
      // console.log('💾 [DUAL STREAM] Сохранение выбора:', { 
      //   modelKey, 
      //   modelId,
      //   lastDisplayMessage: lastDisplayMessage.current,
      //   lastFullMessage: lastFullMessageRef.current?.substring(0, 100) + '...',
      //   displayLength: lastDisplayMessage.current?.length,
      //   fullLength: lastFullMessageRef.current?.length
      // });
      
      // ИСПРАВЛЕНО: Передаем и краткое и полное сообщение (как в обычном streaming)
      const response = await axios.post(`/api/chat/${chatId}/messages/save-choice`, {
        chosenResponse: content,
        modelId: modelId,
        userMessageContent: lastDisplayMessage.current, // Краткое для отображения
        contentForAI: lastFullMessageRef.current // Полное с файлами для сохранения в БД
      });

      console.log('✅ [DUAL STREAM] Выбор сохранен:', response.data);
      resetDualStream();

      return response.data;
    } catch (error) {
      console.error('❌ [DUAL STREAM] Ошибка сохранения выбора:', error);
      throw error;
    }
  };

  const autoChooseFirstOption = async (chatId) => {
    if (!pendingDualChoice) return null;
    // console.log('🤖 [DUAL STREAM] Автоматический выбор первого варианта');
    return await saveChosenResponse(
      chatId, 
      'model1', 
      pendingDualChoice.model1.content, 
      pendingDualChoice.model1.modelId
    );
  };

  useEffect(() => {
    return () => {
      if (dualAbortControllerRef.current) {
        dualAbortControllerRef.current.abort();
      }
    };
  }, []);

  // Новый метод для dual streaming с файлами
  const startDualStreamingWithFiles = async (chatId, files, message, modelIds, systemPrompt = null) => {
    try {
      // console.log('📁 [DUAL STREAM WITH FILES] Начинаем dual streaming с файлами:', { 
      //   chatId, 
      //   filesCount: files.length, 
      //   modelIds 
      // });
      
      resetDualStream();
      setIsDualStreaming(true);
      
      // Устанавливаем текущее сообщение пользователя для отображения в UI
      const userMessage = `📎 ${files.map(f => f.name || f.file?.name || 'файл').join(', ')}${message ? `\n${message}` : ''}`;
      setCurrentUserMessage(userMessage);

      dualAbortControllerRef.current = new AbortController();

      // Создаем FormData для отправки файлов
      const formData = new FormData();
      files.forEach(fileData => {
        const file = fileData.file ? fileData.file : fileData;
        formData.append('files', file);
      });
      
      // Добавляем текстовые параметры
      if (message) formData.append('message', message);
      if (systemPrompt) formData.append('systemPrompt', systemPrompt);
      formData.append('modelIds', JSON.stringify(modelIds));

      const baseURL = API_CONFIG.getBaseURL();
      const response = await fetch(`${baseURL}/api/chat/${chatId}/messages/dual-stream-with-files`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
          // НЕ добавляем Content-Type для FormData
        },
        body: formData,
        signal: dualAbortControllerRef.current.signal
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      const processStream = async () => {
        try {
          let buffer = '';
          
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              console.log('🏁 [DUAL STREAM WITH FILES] Поток завершен');
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Сохраняем неполную строку

            for (const line of lines) {
              if (!line.trim()) continue;
              
              try {
                if (line.startsWith('event:')) {
                  continue;
                }
                
                if (line.startsWith('data:')) {
                  const jsonStr = line.substring(5).trim();
                  if (jsonStr) {
                    const data = JSON.parse(jsonStr);
                    
                    // Специальная обработка событий файлов
                    if (data.type === 'files_processing_start') {
                      // Можем показать прогресс обработки файлов в UI
                      continue;
                    }
                    
                    if (data.type === 'file_processing') {
                      // Можем показать прогресс обработки каждого файла
                      continue;
                    }
                    
                    if (data.type === 'files_processing_complete') {
                      // Файлы обработаны, начинается dual streaming
                      continue;
                    }
                    
                    // Обычная обработка dual streaming событий
                    switch (data.type) {
                      case 'dual_stream_start':
                        setDualStreamingData(prev => ({
                          model1: { ...prev.model1, modelName: data.model1.name, modelId: data.model1.id },
                          model2: { ...prev.model2, modelName: data.model2.name, modelId: data.model2.id }
                        }));
                        break;
                      case 'content':
                        const modelKey = data.modelKey;
                        if (modelKey && (modelKey === 'model1' || modelKey === 'model2')) {
                          setDualStreamingData(prev => ({
                            ...prev,
                            [modelKey]: {
                              ...prev[modelKey],
                              streamingText: data.fullText || prev[modelKey].streamingText + data.content,
                              tokenCount: data.tokenCount || prev[modelKey].tokenCount
                            }
                          }));
                        }
                        break;
                      case 'dual_stream_complete':
                        setIsDualStreaming(false);
                        // Сохраняем userMessage из ответа для правильного отображения в UI
                        lastDisplayMessage.current = data.userMessage || `Файлы: ${files.map(f => f.name || f.file?.name || 'файл').join(', ')}${message ? `\n\n${message}` : ''}`;
                        setPendingDualChoice({
                          model1: { 
                            content: data.responses.model1.content, 
                            modelName: data.responses.model1.modelName, 
                            modelId: dualStreamingData.model1.modelId 
                          },
                          model2: { 
                            content: data.responses.model2.content, 
                            modelName: data.responses.model2.modelName, 
                            modelId: dualStreamingData.model2.modelId 
                          }
                        });
                        break;
                      case 'model_error':
                        if (data.modelKey && (data.modelKey === 'model1' || data.modelKey === 'model2')) {
                          const errorMessage = data.error.includes('Country, region, or territory not supported')
                            ? '🚫 OpenAI API недоступен в вашем регионе. Используйте DeepSeek модели.'
                            : `❌ Ошибка: ${data.error}`;
                          setDualStreamingData(prev => ({
                            ...prev,
                            [data.modelKey]: { ...prev[data.modelKey], streamingText: errorMessage }
                          }));
                        }
                        break;
                      case 'error':
                        setDualStreamingError(data.message || 'Неизвестная ошибка streaming');
                        setIsDualStreaming(false);
                        break;
                      default:
                        break;
                    }
                  }
                }
              } catch (parseError) {
                console.error('❌ [DUAL STREAM WITH FILES] Ошибка парсинга JSON:', parseError, line);
              }
            }
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('❌ [DUAL STREAM WITH FILES] Ошибка чтения потока:', error);
            setDualStreamingError('Ошибка чтения потока данных');
          }
          setIsDualStreaming(false);
        } finally {
          reader.releaseLock();
        }
      };
      
      processStream();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('❌ [DUAL STREAM WITH FILES] Ошибка запуска:', error);
        setDualStreamingError(`Ошибка запуска dual streaming с файлами: ${error.message}`);
        throw error;
      }
      setIsDualStreaming(false);
    }
  };

  return {
    isDualStreaming,
    dualStreamingData,
    dualStreamingError,
    pendingDualChoice,
    currentUserMessage,
    startDualStreaming,
    startDualStreamingWithFiles, // Новый метод
    stopDualStreaming,
    saveChosenResponse,
    autoChooseFirstOption,
    hasPendingChoice: !!pendingDualChoice,
    canStartDualStream: !isDualStreaming,
  };
};

export default useDualStreaming;